import { useState } from 'react'
import { type Dispatch, type SetStateAction } from 'react'

/**
 * Custom hook that handles all sharepoint duration related logic for Sharepoint Drawers.
 * This hook manages:
 * - Unlimited share duration flag (for both Create and Edit modes)
 * - Remaining share duration state (only for Edit mode)
 * - Function to calculate remaining share duration (only for Edit mode)
 *
 * @param isEditMode - Whether the drawer is in edit mode (affects which features are available)
 * @returns Object containing unlimitedShareDuration state, remainingShareDuration state (if edit mode), and utility functions
 */
export function useSharepointDurationHandler(
  isEditMode = false
): {
  unlimitedShareDuration: boolean
  setUnlimitedShareDuration: Dispatch<SetStateAction<boolean>>
  remainingShareDuration: string
  setRemainingShareDuration: Dispatch<SetStateAction<string>>
  getRemainingShareDuration: (sharepoint?: { shareDuration?: number | string; createdAt: Date }) => number
  resetSharepointDuration: () => void
} {
  const [unlimitedShareDuration, setUnlimitedShareDuration] = useState(false)
  const [remainingShareDuration, setRemainingShareDuration] = useState<string>('')

  /**
   * Determines the remaining number of days that a sharepoint is active.
   * Only available in edit mode.
   * @param sharepoint - The sharepoint object to calculate remaining duration for
   * @returns The number of remaining days (0 if unlimited or undefined)
   */
  function getRemainingShareDuration(sharepoint?: { shareDuration?: number | string; createdAt: Date }): number {
    if (!sharepoint) return 0
    const duration = typeof sharepoint.shareDuration === 'string'
      ? Number(sharepoint.shareDuration)
      : sharepoint.shareDuration

    if (typeof duration === 'undefined' || duration === 0) {
      return 0
    }
    const today = new Date()
    const remainingDays =
      (sharepoint.createdAt.valueOf() +
        (duration ?? 0) * 24 * 60 * 60 * 1000 -
        today.valueOf()) /
      1000 /
      60 /
      60 /
      24
    return Math.round(remainingDays)
  }

  /**
   * Resets sharepoint duration to initial state (used in handleClose).
   */
  const resetSharepointDuration = () => {
    setUnlimitedShareDuration(false)
    setRemainingShareDuration('')
  }

  return {
    unlimitedShareDuration,
    setUnlimitedShareDuration,
    remainingShareDuration,
    setRemainingShareDuration,
    getRemainingShareDuration,
    resetSharepointDuration,
  }
}
